import { NgTemplateOutlet } from "@angular/common";
import { ChangeDetectionStrategy, Component, contentChild, inject, input, TemplateRef } from "@angular/core";
import { BaseComponent } from "primeng/basecomponent";
import { PageheaderStyle } from "./pageheader.style";

/**
 * Shows page header in a canonical way. Use it on top of every page.
 *
 * Action controls can be projected into the component.
 *
 * @example
 * ```html
 * <div class="page">
 *   <ui-pageheader
 *     [caption]="t('candidates.title')"
 *     [description]="t('candidates.description')"
 *   >
 *     <menu class="row">
 *       <li>
 *         <p-button [label]="t('candidate.new')" />
 *       </li>
 *     </menu>
 *   </ui-pageheader>
 *
 *   <article class="content">
 *     ...
 *   </article>
 * </div>
 * ```
 */
@Component({
  selector: "ui-pageheader",
  templateUrl: "./pageheader.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { "[class]": "cx('root')" },
  imports: [NgTemplateOutlet],
})
export class PageheaderComponent extends BaseComponent {
  protected readonly _componentStyle = inject(PageheaderStyle);

  /**
   * Compact title of the page.
   */
  readonly caption = input.required<string>();

  /**
   * Detailed information about the page.
   */
  readonly description = input<string>();

  /**
   * Detail information about the page provided as ng-template.
   */
  readonly descriptionTemplate = contentChild<TemplateRef<void>>("description");

  /**
   * The compact title provided as ng-template, in place of the plain {@link caption} text - lets a
   * caller put something other than plain text next to the title, e.g. an inline edit button.
   */
  readonly captionTemplate = contentChild<TemplateRef<void>>("caption");
}
